/*
 * Mixpanel pelo SDK oficial (`mixpanel-browser`), no lugar do Pocket.
 *
 * Este é o ÚNICO ponto do app que fala com o Mixpanel. O Pocket continua carregado
 * (HubSpot, GTM, Zé Spot, Auth, profile), mas não carrega nem envia mais nada ao
 * Mixpanel: o `POCKET_ENV` não leva mais o token e toda chamada ao Pocket vai com
 * `POCKET_WITHOUT_MIXPANEL`.
 *
 * O que o Pocket fazia, e foi reproduzido aqui:
 *   - carregava `https://mixpanel.paas.descomplica.com.br/lib.min.js`, que é byte a
 *     byte o `mixpanel.min.js` da 2.84.0. A dependência está travada nessa versão,
 *     então os defaults (autocapture, track_pageview e session replay desligados,
 *     persistência em cookie de `.descomplica.com.br`) são os mesmos de antes;
 *   - `mixpanel.init(token, { api_host: <proxy> })`, com as opções de heatmap só
 *     quando `enableHeatmaps` (o admissions nunca ligou);
 *   - `identify`, `alias`, `registerMixpanel`/`unregisterMixpanel`, `track`,
 *     `trackLinks`, `timeEvent` e `reset`, com o mesmo mapeamento de propriedades
 *     (`formatMixpanelProps`/`getMixpanelPeopleProps`) e a mesma lista de chaves que
 *     nunca saem (`TELEMETRY_DENYLIST`).
 *
 * O build importado é o "async modules": sem o gravador de sessão embutido (o
 * `mixpanel-browser` padrão traz o rrweb inteiro no bundle). Se o session replay for
 * ligado, o SDK baixa o gravador sob demanda da CDN do Mixpanel, como a lib do proxy.
 */
import mixpanelBrowser, {
  Config,
  Dict,
  Mixpanel,
  Query,
} from 'mixpanel-browser/dist/mixpanel-with-async-modules.cjs';

export const MIXPANEL_PROXY_HOST = 'https://mixpanel.paas.descomplica.com.br';

/*
 * `enableHeatmaps` do Pocket. Ele também mandava `session_recording: { mask_all_text:
 * false }`, mas essa chave não existe na 2.84.0 (o equivalente é `record_mask_all_text`)
 * e era ignorada: o texto continuava mascarado. Não foi trazida para não desmascarar,
 * nas gravações, o nome e o e-mail que as telas mostram (confirmação de conta, perfil).
 */
export const HEATMAP_DEFAULTS: Partial<Config> = {
  record_sessions_percent: 1,
  record_heatmap_data: true,
  autocapture: false,
};

export interface MixpanelInitOptions {
  enableHeatmaps?: boolean;
  heatmapsConfig?: Partial<Config>;
}

export type MixpanelProperties = Record<string, any>;

// `cookie` é o apelido interno de `persistence` no SDK: fica fora dos tipos públicos.
// É dele que saem as super properties (`cookie.props`) e o `cookie.clear()` do reset.
export type MixpanelInstance = Mixpanel & {
  cookie?: {
    props?: MixpanelProperties;
    clear?: () => void;
  };
};

// Opções de toda chamada ao Pocket: ele lê `window.mixpanel` quando recebe
// `useMixpanel: true`, e essa instância agora é a daqui, então mandaria em dobro.
export const POCKET_WITHOUT_MIXPANEL = { useMixpanel: false };

/*
 * Chaves que NUNCA saem para o Mixpanel (cópia da lista do Pocket). O cookie `d` é o
 * token de sessão do aluno; até 02/09/2026 ele viajava em texto puro como super
 * property quando alguém chamava `identify` com o perfil inteiro.
 */
const TELEMETRY_DENYLIST = [
  'd',
  'd-profile',
  'token',
  'accesstoken',
  'access_token',
  'refreshtoken',
  'refresh_token',
  'authorization',
  'password',
  'senha',
];

// propriedades reservadas do Mixpanel, comparadas em minúsculas
const RESERVED_PROPS: Record<string, string> = {
  created: '$created',
  email: '$email',
  name: '$name',
  firstname: '$firstName',
  lastname: '$lastName',
  phone: '$phone',
};

// só estas chaves viram propriedade reservada no perfil (`people.set`)
const PEOPLE_RESERVED_KEYS = [
  'created',
  'email',
  'name',
  'firstName',
  'lastName',
  'phone',
  '$created',
  '$email',
  '$name',
  '$firstname',
  '$lastname',
  '$phone',
  'is_subscriber',
  'Cpf',
  'PaymentId',
  'PaymentType',
  'ProductName',
  'ProductValue',
  'promocode',
];

const isSensitiveProp = (key: string): boolean =>
  TELEMETRY_DENYLIST.includes(String(key).toLocaleLowerCase());

/** Propriedades de evento e super properties, como o `formatMixpanelProps` do Pocket. */
export const formatMixpanelProps = (props?: MixpanelProperties): MixpanelProperties | undefined => {
  if (!props) return props;

  return Object.keys(props).reduce<MixpanelProperties>((formatted, key) => {
    if (isSensitiveProp(key)) return formatted;

    const reservedKey = RESERVED_PROPS[key.toLocaleLowerCase()];
    return { ...formatted, [reservedKey || key]: props[key] };
  }, {});
};

/** Propriedades de perfil, como o `getMixpanelPeopleProps` do Pocket. */
export const getMixpanelPeopleProps = (props?: MixpanelProperties): MixpanelProperties | null => {
  if (!props || Object.keys(props).length === 0) return null;

  const peopleProps = Object.keys(props).reduce<MixpanelProperties>((formatted, key) => {
    if (isSensitiveProp(key)) return formatted;

    const reservedKey = PEOPLE_RESERVED_KEYS.includes(key)
      ? RESERVED_PROPS[key.toLocaleLowerCase()]
      : undefined;
    return { ...formatted, [reservedKey || key]: props[key] };
  }, {});

  // se sobrou só o que não pode sair, não há perfil para atualizar
  return Object.keys(peopleProps).length ? peopleProps : null;
};

export const buildMixpanelConfig = ({
  enableHeatmaps = false,
  heatmapsConfig = {},
}: MixpanelInitOptions = {}): Partial<Config> => {
  const config: Partial<Config> = { api_host: MIXPANEL_PROXY_HOST };

  return enableHeatmaps ? { ...config, ...HEATMAP_DEFAULTS, ...heatmapsConfig } : config;
};

let instance: MixpanelInstance | null = null;

/**
 * Inicializa o Mixpanel uma vez e expõe a instância em `window.mixpanel`: o GTM lê de
 * lá (`get_distinct_id`, `get_property`, `register` do primeiro/último clique) e
 * dispara as próprias tags com `mixpanel.track`. Sem token, nada é inicializado e
 * todas as funções abaixo viram no-op.
 */
export const initMixpanel = (
  token?: string,
  options?: MixpanelInitOptions,
): MixpanelInstance | null => {
  if (instance) return instance;
  if (typeof window === 'undefined' || !token) return null;

  // no build de módulo o `init` sem nome inicializa o próprio objeto exportado
  mixpanelBrowser.init(token, buildMixpanelConfig(options));
  instance = mixpanelBrowser as MixpanelInstance;
  window.mixpanel = instance;

  return instance;
};

// o `typeof` vem do Pocket: id ausente em runtime (usuário sem e-mail no store) não vira identify
export const identify = (userId?: string, traits?: MixpanelProperties): void => {
  if (!instance) return;

  if (typeof userId === 'string') instance.identify(userId);

  if (traits) {
    const peopleProps = getMixpanelPeopleProps(traits);
    if (peopleProps) instance.people.set(peopleProps);
    // o Pocket também registrava os traits como super properties
    instance.register(formatMixpanelProps(traits) as Dict);
  }
};

export const alias = (userId?: string): void => {
  if (instance && typeof userId === 'string') instance.alias(userId);
};

/** `POCKET.registerMixpanel`: super properties sem formatação. */
export const register = (props: MixpanelProperties): void => {
  if (instance) instance.register(props);
};

/** `POCKET.unregisterMixpanel`. */
export const unregister = (propName: string): void => {
  if (instance) instance.unregister(propName);
};

export const track = (eventName: string, props?: MixpanelProperties): void => {
  if (instance) instance.track(eventName, formatMixpanelProps(props));
};

export const trackLinks = (
  query: Query,
  eventName: string,
  props?: Dict | ((element: Element) => Dict),
): void => {
  // os tipos do SDK declaram a função sem o elemento, mas ele é passado;
  // `track_links`/`time_event` são os nomes da API do SDK
  // eslint-disable-next-line camelcase
  if (instance) instance.track_links(query, eventName, props as Dict);
};

export const timeEvent = (eventName: string): void => {
  // eslint-disable-next-line camelcase
  if (instance) instance.time_event(eventName);
};

/** `POCKET.reset`: apaga o cookie do Mixpanel (`cookie.clear()`), como o Pocket fazia. */
export const reset = (): void => {
  if (instance && instance.cookie && instance.cookie.clear) instance.cookie.clear();
};

/** Super properties persistidas no cookie; o `TRACK_EVENT` as repassa ao ug-enrollments. */
export const getSuperProperties = (): MixpanelProperties | undefined => instance?.cookie?.props;
